<template>
  <div class="w-full h-96 flex justify-center items-center">
    <NuxtImg class="w-20" src="/icons/loading.svg" />
  </div>
</template>

<script setup lang="ts">
import type { GetStartedService } from "../../types";

const route = useRoute();
const getStartedModalStore = useGetStartedModalStore();
const mapStore = useMapStore();

/**
 * Selected service entity.
 */
const selectedService = computed(() => getStartedModalStore.selectedService);

/**
 * Get clinic slug from route query params.
 */
const selectedClinicSlug = computed(() => route.query.slug);

/**
 * Fetch clinic detail by selected slug.
 */
async function fetchClinicDetailBySlug() {
  if (!selectedClinicSlug.value) return;

  const { data: clinicDetail } = await useFetchClinicDetailBySlug(selectedClinicSlug.value);
  const clinic = clinicDetail.value?.data;
  if (!clinic) return;

  mapStore.setSelectedClinic({
    id: clinic.id,
    title: clinic.display_title,
    slug: clinic.slug,
  });
  await fetchClinicTests(clinic.id);
}

/**
 * Fetch all tests related to a specific clinic.
 * Get started → clinic test should show both `clinic-test` and `instant-test` products,
 * but `instant-test` is only requested when that service is enabled site-wide so
 * deployments without it keep their previous behaviour.
 * @param clinic - Clinic Id from where we should fetch tests.
 */
const fetchClinicTests = async (clinic: string) => {
  mapStore.setPanelTestsLoading(true);
  try {
    const { data: servicesResult } = await useServiceListType();
    const services: GetStartedService[] = servicesResult.value?.data ?? [];
    const isInstantTestEnabled = services.some((service) => service.slug === "instant-test");

    const params: Record<string, string> = {
      clinic,
      "service_type[0]": "clinic-test",
    };
    if (isInstantTestEnabled) {
      params["service_type[1]"] = "instant-test";
    }

    const { data: productsList } = await useFetchProductList(params);
    mapStore.getTests(productsList.value?.data ?? []);
  } finally {
    mapStore.setPanelTestsLoading(false);
  }
};

/**
 * Watch selected clinic slug change in order to update the products.
 */
watch(selectedClinicSlug, fetchClinicDetailBySlug);
await fetchClinicDetailBySlug();

/**
 * Open modal with selected service and clinic view.
 */
function openModal() {
  getStartedModalStore.navigate("select_clinic");
  getStartedModalStore.open({
    title: "",
    slug: "",
    clinic_view: "map",
    ...selectedService.value,
    content: "",
  });
}

/**
 * Abort navigation and reset service param.
 */
async function abort() {
  navigateTo({
    params: {
      ...route.params,
      service: "",
    },
  });
}

/**
 * Watch for modal open state changes to trigger abort if closed.
 */
watch(
  () => getStartedModalStore.isOpen,
  (isOpen, wasOpen) => {
    if (wasOpen && !isOpen) abort();
  }
);

/**
 * Oen/Close modal on component mount/unmount.
 */
onBeforeUnmount(getStartedModalStore.close);
onBeforeMount(openModal);
</script>
